import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { campaignSchema, describeCampaign } from "@/lib/campaign";
import { CONSENT_COOKIE, resolveConsent } from "@/lib/consent";

const submissionSchema = z.object({
  name: z.string().trim().min(2).max(100),
  email: z.string().trim().email().max(255),
  website: z.string().trim().min(3).max(255),
  spend: z.string().trim().min(1).max(100),
  // Optional. Defaulted so a page loaded before these fields existed can
  // still submit during a deploy.
  notes: z.string().trim().max(1000).default(""),
  whatsapp: z
    .string()
    .trim()
    .max(30)
    .regex(/^[+\d\s().-]*$/)
    .default(""),
  // Minted by the browser so the pixel's Lead and the Conversions API copy
  // below share one id; Meta deduplicates on it.
  eventId: z.string().trim().min(1).max(100),
  // Which ad the visitor arrived by. Defaulted for the same reason as notes.
  campaign: campaignSchema.default({}),
});

export const submitAuditRequest = createServerFn({ method: "POST" })
  .validator((data: unknown) => submissionSchema.parse(data))
  .handler(async ({ data }) => {
    const { getDb } = await import("@/lib/db.server");

    // Save first. The database is the record of truth for a lead; a
    // notification is only a convenience on top of it.
    try {
      await getDb().query(
        "INSERT INTO audit_requests (name, email, website, monthly_spend, notes, whatsapp, utm_source, utm_medium, utm_campaign, utm_content) VALUES ($1, $2, $3, $4, $5, $6, $7, $8, $9, $10)",
        [
          data.name,
          data.email,
          data.website,
          data.spend,
          data.notes || null,
          data.whatsapp || null,
          data.campaign.source ?? null,
          data.campaign.medium ?? null,
          data.campaign.campaign ?? null,
          data.campaign.content ?? null,
        ],
      );
    } catch (error) {
      console.error("[audit-request] insert failed:", error);
      throw new Error("Could not save your request. Please try again.");
    }

    const { getCookie, getRequestHeader, getRequestIP, getRequestUrl } =
      await import("@tanstack/react-start/server");

    // The server-side copy of the Lead event is measurement, and measurement
    // waits for the same answer the pixel waits for. Without consent there is
    // no _fbp to match on anyway, and Meta would be handed a hashed email it
    // was never allowed to have.
    if (resolveConsent(getRequestHeader("cf-ipcountry"), getCookie(CONSENT_COOKIE)) === "allowed") {
      const { sendLeadEvent } = await import("@/lib/meta-capi.server");
      await sendLeadEvent({
        eventId: data.eventId,
        email: data.email,
        spend: data.spend,
        sourceUrl: getRequestUrl().toString(),
        clientIp: getRequestIP({ xForwardedFor: true }),
        userAgent: getRequestHeader("user-agent"),
        fbp: getCookie("_fbp"),
        fbc: getCookie("_fbc"),
      });
    }

    const { notifyNewLead, sendEnquiryConfirmation } = await import("@/lib/notify.server");

    // One confirmation per address per day. The form is public: without a
    // limit, anyone could make adkiro.com send mail to any address typed into
    // it, as often as they liked — and it would be Adkiro's domain reputation
    // that paid for it. The row just inserted is counted, hence "> 1".
    const confirm = async (): Promise<boolean> => {
      try {
        const { rows } = await getDb().query<{ n: number }>(
          "SELECT count(*)::int AS n FROM audit_requests WHERE lower(email) = lower($1) AND created_at > now() - interval '24 hours'",
          [data.email],
        );
        if ((rows[0]?.n ?? 0) > 1) return false;
        return await sendEnquiryConfirmation(data.email, data.name, {
          whatsapp: data.whatsapp !== "",
        });
      } catch (error) {
        console.error("[audit-request] confirmation failed:", error);
        return false;
      }
    };

    // In parallel: the prospect's form is waiting on this request.
    const [sent, confirmed] = await Promise.all([
      notifyNewLead("audit-request", {
        title: `New account review fit request — ${data.name}`,
        replyTo: data.email,
        fields: [
          ["Name", data.name],
          ["Email", data.email],
          ["WhatsApp", whatsappLine(data.whatsapp)],
          ["Website", data.website],
          ["Monthly ad spend", data.spend],
          ["Review focus", data.notes || "(not given)"],
          ["Came from", describeCampaign(data.campaign)],
        ],
      }),
      confirm(),
    ]);

    return { saved: true, notified: sent.telegram || sent.email, confirmed };
  });

// Typed with a country code, the number becomes a tap-to-chat link in the alert.
function whatsappLine(value: string): string {
  if (!value) return "(not given)";
  const digits = value.replace(/\D/g, "");
  const international = value.startsWith("+")
    ? digits
    : value.startsWith("00")
      ? digits.slice(2)
      : "";
  return international ? `${value} — https://wa.me/${international}` : value;
}
